/**
 * result-v2/registry.ts
 * ---------------------------------------------------------------------------
 * Maps a SearchResult onto the template that should render it.
 *
 * Resolution order:
 *   1. shape of the payload   (a ProfileData entity beats the `type` field)
 *   2. sub-type in `sources[]` (ipgeo_number splits into two validators)
 *   3. `type`
 *   4. fallback: PersonTemplate, which degrades gracefully
 *
 * Adding a workflow is one line in TYPE_MAP. Adding a NEW SHAPE is one entry
 * in `resolveTemplate`.
 */
import * as React from 'react'
import { SearchResult, Type } from '../../store/searcher/reducer'
import { PersonTemplate } from './templates/PersonTemplate'
import { BreachTemplate } from './templates/BreachTemplate'
import { NameScanTemplate } from './templates/NameScanTemplate'
import { FaceTemplate } from './templates/FaceTemplate'
import { GeoTemplate } from './templates/GeoTemplate'
import { SceneTemplate } from './templates/SceneTemplate'
import { PsychoTemplate } from './templates/PsychoTemplate'
import { SentimentTemplate } from './templates/SentimentTemplate'
import { ValidatorTemplate } from './templates/ValidatorTemplate'
import { SocialProfileTemplate } from './templates/SocialProfileTemplate'
import { PostsTemplate } from './templates/PostsTemplate'
import { RedditTemplate } from './templates/RedditTemplate'
import { CompanyReportTemplate } from './templates/CompanyReportTemplate'
import { CryptoWalletTemplate } from './templates/CryptoWalletTemplate'
import { RegionMonitoringTemplate } from './templates/RegionMonitoringTemplate'

export type TemplateProps = {
  apiData: SearchResult
  /** optional real map, injected by ResultView so templates stay dependency-free */
  mapSlot?: React.ReactNode
}

export type TemplateId =
  | 'person' | 'breach' | 'namescan' | 'face' | 'geo'
  | 'scene' | 'psycho' | 'sentiment' | 'validator' | 'social' | 'posts' | 'reddit' | 'company' | 'crypto' | 'region'

export const TEMPLATES: Record<TemplateId, React.ComponentType<TemplateProps>> = {
  person: PersonTemplate,
  breach: BreachTemplate,
  namescan: NameScanTemplate,
  face: FaceTemplate,
  geo: GeoTemplate,
  scene: SceneTemplate,
  psycho: PsychoTemplate,
  sentiment: SentimentTemplate,
  validator: ValidatorTemplate,
  social: SocialProfileTemplate,
  posts: PostsTemplate,
  reddit: RedditTemplate,
  company: CompanyReportTemplate,
  crypto: CryptoWalletTemplate,
  region: RegionMonitoringTemplate,
}

/** Human title shown in the result header. Mirrors PopupCommon's old labels. */
export const TITLE_BY_TYPE: Partial<Record<Type | string, string>> = {
  combined_phone: 'Phone Lookup',
  phone: 'Phone Lookup',
  real_phone: 'Real Person Or Not',
  combined_email: 'E-mail Lookup',
  combined_name: 'Name SocialScan',
  name: 'Name Lookup',
  name_api: 'Name Lookup',
  name_by_country: 'Name by Country',
  combined_id: 'ID WebScan',
  deepweb: 'BreachScan',
  face_search: 'Face WebScan',
  photo_searcher: 'Sniper API results',
  image_insize: 'Image Analysis',
  irbis_eye: 'SceneCheck',
  sentiment_analysys: 'AI Sentiment Analysis',
  psycho_profile: 'Psychological Portrait',
  ip_geo: 'IP Geolocation',
  ipgeo_number: 'IRBIS Validation',
  web_collection: 'Web Collection',
  kyc: 'KYC',
  group_members: 'Group Members',
  post: 'Posts observer results',
  email_list: 'E-mail Bulk Lookup',
  phone_list: 'Phone Bulk Lookup',
  public_company_info: 'Company Search',
  crypto_wallet_screening: 'Crypto Wallet Screening',
  region_monitoring: 'Region Monitoring',
}

const TYPE_MAP: Partial<Record<Type | string, TemplateId>> = {
  combined_phone: 'person',
  phone: 'person',
  real_phone: 'person',
  combined_email: 'person',
  name_api: 'person',
  combined_name: 'namescan',
  name: 'namescan',
  name_by_country: 'namescan',
  deepweb: 'breach',
  face_search: 'face',
  photo_searcher: 'face',
  ip_geo: 'geo',
  image_insize: 'scene',
  irbis_eye: 'scene',
  psycho_profile: 'psycho',
  sentiment_analysys: 'sentiment',
  ipgeo_number: 'validator',
  combined_id: 'social',
  post: 'posts',
  public_company_info: 'company',
  crypto_wallet_screening: 'crypto',
  region_monitoring: 'region',
}

const entitiesOf = (row: any): any[] => (Array.isArray(row?.apiDataEntities) ? row.apiDataEntities : [])
const sourceNames = (row: any): string[] =>
  (Array.isArray(row?.sources) ? row.sources : []).map((s: any) => String(s?.name ?? s).toLowerCase())

/**
 * Webmii's two entities carry their own `profileData`/`profile` object on
 * every row (see NameScanTemplate.tsx's extractWebmii/extractWebmiiSocial),
 * but that object has nothing to do with the Instagram-style counters
 * (`posts_count`/`followers_count`/...) the shape-detectors below are
 * actually looking for. A bare `r.profileData` truthy check can't tell the
 * two apart, so without this exclusion a combined_name result containing a
 * webmii entity gets hijacked to SocialProfileTemplate — which renders
 * nothing but dashes for a shape it doesn't understand — before it ever
 * reaches NameScanTemplate, which does. Webmii rows are handled entirely
 * inside NameScanTemplate, so they should never influence routing here.
 */
const isWebmiiEntity = (e: any): boolean => {
  const from = String(e?.from || '').toLowerCase()
  return from === 'webmii' || from === 'webmii_social'
}

/** A row describing a social profile, not a leaked credential. */
const rowIsProfile = (r: any): boolean =>
  !!r && typeof r === 'object' &&
  (r.type === 'ProfileItem' || r.type === 'ProfileData' || !!r.social_network || !!r.profile_id || !!r.profileData)

/**
 * Every shape-detector below assumes `entity.data` already IS the row
 * array. `irbis-social-tiktok` (and presumably any sibling `irbis-social-*`
 * source built the same way) instead wraps it one level deeper:
 * `entity.data` is a single-item array holding `{ ok, source, totalItems,
 * data: [ ...real ProfileItem/PostItem rows... ] }` — a summary envelope,
 * not a row. Unwrapped, that inner `data` is the exact ProfileItem+PostItem
 * sibling shape `looksLikeProfileData`/SocialProfileTemplate.tsx's own
 * `extract()` already understand (see combined_id's `insta_story`); left
 * wrapped, every row-shape check below silently tests the envelope object
 * instead (no `type`/`social_network`/`profileData` on IT) and finds
 * nothing.
 */
export const rowsOf = (e: any): any[] => {
  const data = e?.data
  if (!Array.isArray(data)) return []
  const only = data.length === 1 ? data[0] : null
  if (only && typeof only === 'object' && Array.isArray(only.data) && !rowIsProfile(only) && !('password' in only)) {
    return only.data
  }
  return data
}

/**
 * True only when a row actually carries a leaked CREDENTIAL.
 *
 * Two traps here, both hit in real payloads:
 *  - `combined_phone` ships an entity with `from: "PLUTO"` and `data: []`.
 *  - `combined_email` and `combined_name` ship PLUTO entities whose rows are
 *    ordinary broker person records or ProfileItems, with no password at all.
 * Trusting the provider name routed E-mail Lookup and Name SocialScan to the
 * BreachScan template. Only a password or hash counts.
 */
const rowIsLeak = (r: any): boolean =>
  !!r && typeof r === 'object' && !rowIsProfile(r) &&
  ('password' in r || 'passwordHash' in r || 'hash' in r || 'Password' in r)

const looksLikeLeakRecords = (row: any): boolean =>
  entitiesOf(row).some((e) => {
    if (isWebmiiEntity(e)) return false
    return rowsOf(e).some(rowIsLeak)
  })

/** True when any entity row is a single social profile with counters. */
const looksLikeProfileData = (row: any): boolean =>
  entitiesOf(row).some((e) => {
    if (isWebmiiEntity(e)) return false
    return rowsOf(e).some((r: any) => r && typeof r === 'object' && (r.type === 'ProfileData' || r.profileData))
  })

/** True when any entity row is a list of candidate profiles. */
const looksLikeProfileList = (row: any): boolean =>
  entitiesOf(row).filter((e) => {
    if (isWebmiiEntity(e)) return false
    return rowsOf(e).filter((r: any) => r && typeof r === 'object' && r.type === 'ProfileItem').length > 0
  }).length > 0

/**
 * True when any entity row carries the Reddit AI-report shape. Reddit
 * (combined_id sub-source `reddit`/`redditid`) doesn't return
 * counters-and-media like insta_story — it returns an AI-generated
 * `report.profile_assessment` object (see RedditTemplate.tsx's header
 * comment for the full field breakdown, sourced from the old UI's
 * RedditResultsViewer.tsx). Routing on this shape rather than only on
 * `sources[]` naming matters here specifically: the old UI's own routing
 * (NameCommon.tsx) only ever checked `item.source === 'reddit'`, silently
 * missing the `redditid` variant that irbispro-v2/config/sources.ts
 * documents as an equally valid source name for the same workflow — a shape
 * check catches both without needing to enumerate every alias.
 *
 * One more variant seen from the scraping pipeline: a `ProfileItem` row
 * whose report lives at `profileData.report` instead of directly on the
 * row (siblings are raw `PostItem` posts, not reports, so they never match
 * here). Checked explicitly so this case isn't shadowed by
 * `looksLikeProfileData` below, which would otherwise claim any row with a
 * `profileData` field — including this one — for SocialProfileTemplate.
 */
const hasRedditProfileAssessment = (r: any): boolean =>
  !!r && typeof r === 'object' && (
    (r.report && typeof r.report === 'object' && r.report.profile_assessment) ||
    (r.profileData && typeof r.profileData === 'object' && r.profileData.report && typeof r.profileData.report === 'object' && r.profileData.report.profile_assessment)
  )

const looksLikeRedditReport = (row: any): boolean =>
  entitiesOf(row).some((e) => rowsOf(e).some(hasRedditProfileAssessment))

export function resolveTemplateId(row: SearchResult | null | undefined): TemplateId {
  if (!row) return 'person'
  const type = String((row as any).type ?? '')
  const sources = sourceNames(row)

  // 1. shape wins over declared type
  if (looksLikeLeakRecords(row)) return 'breach'
  if (looksLikeRedditReport(row)) return 'reddit'
  if (looksLikeProfileData(row)) return 'social'
  if (looksLikeProfileList(row)) return 'namescan'

  // 2. sub-type disambiguation
  if (type === 'ipgeo_number') return 'validator'
  if (type === 'combined_id') {
    if (sources.some((s) => ['reddit', 'redditid'].includes(s))) return 'reddit'
    if (sources.some((s) => ['insta_story', 'instanavigation_person', 'glook_id', 'threads'].includes(s))) return 'social'
    // reverse_phone is a contact-record provider (names/emails/phones/
    // locations), same shape PersonTemplate already renders for
    // phone/combined_phone — not a leaked-credential row, so it must not
    // fall through to the BreachTemplate default below.
    if (sources.some((s) => s === 'reverse_phone')) return 'person'
    // Snapchat is a contact-record provider (name/handle/bio/photos), same
    // shape PersonTemplate already renders for reverse_phone above — not a
    // leaked-credential row, so it must not fall through to BreachTemplate.
    if (sources.some((s) => s === 'snapchat')) return 'person'
    return 'breach'
  }

  // 3. declared type
  return TYPE_MAP[type] ?? 'person'
}

export const resolveTemplate = (row: SearchResult | null | undefined): React.ComponentType<TemplateProps> =>
  TEMPLATES[resolveTemplateId(row)]

export const titleFor = (row: SearchResult | null | undefined): string => {
  const type = String((row as any)?.type ?? '')
  return (row as any)?.requestCaption || TITLE_BY_TYPE[type] || 'Search result'
}
